import type { Category } from '../types';

/**
 * "Food safety & handling" — GL Plus's own operational guidance (work half).
 * Temperature figures follow the standard USDA danger-zone guidance.
 */
export const foodSafety: Category = {
  slug: "food-safety",
  half: "work",
  accent: '#343F5B',
  icon: "percent",
  label: { en: "Food safety & handling", es: "Seguridad e higiene de alimentos" },
  blurb: {
    en: "Cold-chain basics, cooler practice, raw-meat separation — and why the route runs frozen-last.",
    es: "Lo básico de la cadena de frío, el uso de la hielera, la separación de carne cruda — y por qué la ruta deja lo congelado para el final.",
  },
  guides: [
    {
      slug: "cold-chain-basics",
      related: [
        "food-safety/why-frozen-loads-last",
        "food-safety/hot-weather-judgment",
        "your-gear/coolers-and-bags",
        "reporting-a-problem/what-never-counts-against-you",
      ],
      body: {
        en: {
          title: "Cold-chain basics",
          summary:
            "Keeping cold things cold from the shelf to the door is the single most important thing you do for your customers.",
          sections: [
            {
              heading: "You are the last link",
              paragraphs: [
                "Food that needs refrigerating has been kept cold from the farm to the store shelf. The moment it goes into your cart, you are the last link in that chain. Your job is to keep it cold until it is in your customer's hands.",
              ],
            },
            {
              heading: "The danger zone",
              paragraphs: [
                "Bacteria grow fastest between roughly 40°F and 140°F (4–60°C). Perishables should not sit in that range for more than 2 hours, or 1 hour when it is above 90°F (32°C). A parked car in summer can pass that mark quickly.",
              ],
            },
            {
              heading: "How to keep things cold",
              bullets: [
                "Pack cold and frozen items in insulated bags or coolers with ice packs.",
                "Never leave cold bags in a hot vehicle while you do another stop.",
                "Keep coolers closed. Every opening lets warm air in.",
                "Shop cold items last and get them into the cooler right away.",
                "Pack frozen items together. They keep each other cold.",
              ],
            },
            {
              heading: "When in doubt",
              paragraphs: [
                "If an item is warm to the touch, a package is swollen or leaking, or the ice cream has gone soft, do not deliver it. Mark it as an exception in the app. A marked exception never counts against you. A sick customer is a very different story.",
              ],
            },
          ],
          actions: [
            "Set up one cooler and one insulated bag in your vehicle before your next run.",
            "Freeze a set of ice packs, and keep a spare set ready.",
            "On your next run, time how long cold items sit between the register and the door. Aim for under an hour.",
          ],
        },
        es: {
          title: "Lo básico de la cadena de frío",
          summary:
            "Mantener frío lo que debe estar frío, del estante a la puerta, es lo más importante que haces por tus clientes.",
          sections: [
            {
              heading: "Tú eres el último eslabón",
              paragraphs: [
                "Los alimentos que necesitan refrigeración se han mantenido fríos desde la granja hasta el estante de la tienda. En cuanto entran a tu carrito, tú eres el último eslabón de esa cadena. Tu trabajo es mantenerlos fríos hasta que lleguen a manos de tu cliente.",
              ],
            },
            {
              heading: "La zona de peligro",
              paragraphs: [
                "Las bacterias crecen más rápido entre aproximadamente 40°F y 140°F (4–60°C). Los alimentos perecederos no deben estar en ese rango más de 2 horas, o 1 hora cuando hay más de 90°F (32°C). Un auto estacionado en verano puede pasar ese límite muy rápido.",
              ],
            },
            {
              heading: "Cómo mantener todo frío",
              bullets: [
                "Empaca los artículos fríos y congelados en bolsas térmicas o hieleras con paquetes de hielo.",
                "Nunca dejes las bolsas frías en un vehículo caliente mientras haces otra parada.",
                "Mantén las hieleras cerradas. Cada vez que las abres entra aire caliente.",
                "Compra los artículos fríos al final y ponlos en la hielera de inmediato.",
                "Empaca los congelados juntos. Se mantienen fríos entre sí.",
              ],
            },
            {
              heading: "En caso de duda",
              paragraphs: [
                "Si un artículo está tibio al tacto, un empaque está inflado o gotea, o el helado se ablandó, no lo entregues. Márcalo como excepción en la app. Una excepción marcada nunca cuenta en tu contra. Un cliente enfermo es otra historia muy distinta.",
              ],
            },
          ],
          actions: [
            "Prepara una hielera y una bolsa térmica en tu vehículo antes de tu próximo recorrido.",
            "Congela un juego de paquetes de hielo y ten otro de repuesto listo.",
            "En tu próximo recorrido, mide cuánto tiempo pasan los artículos fríos entre la caja y la puerta. Procura que sea menos de una hora.",
          ],
        },
      },
    },
    {
      slug: "why-frozen-loads-last",
      related: [
        "food-safety/cold-chain-basics",
        "food-safety/hot-weather-judgment",
        "reporting-a-problem/what-never-counts-against-you",
      ],
      body: {
        en: {
          title: "Why the route runs frozen-last",
          summary:
            "The app orders your stops shelf-stable, then refrigerated, then frozen, so the coldest items spend the least time in your vehicle.",
          sections: [
            {
              heading: "How the app sequences a run",
              paragraphs: [
                "When you have several stops in one run, the app orders them by temperature: shelf-stable first, refrigerated next, frozen last. Cold items spend the least possible time in the vehicle.",
                "It is not always the shortest path. It is the safest one for the food.",
              ],
            },
            {
              heading: "Why it matters",
              paragraphs: [
                "A bag of rice does not care if it rides around for two hours. A pint of ice cream does. Frozen items warm faster than refrigerated ones once they leave the freezer aisle, so picking them up last means they are first out the door.",
              ],
            },
            {
              heading: "Inside the store, the same rule",
              bullets: [
                "Shop dry goods first.",
                "Refrigerated items second.",
                "Frozen items last, straight into the cooler.",
                "At the register, keep cold bags together so they go into the cooler as one.",
              ],
            },
            {
              heading: "Trust the sequence",
              paragraphs: [
                "If you are tempted to reorder stops to save a few minutes, think about what is in the cooler. A spoiled delivery is a complaint. A followed route is not.",
                "If a stop cannot be completed, for example the customer is not home, mark the exception in the app and the app re-sequences what is left.",
              ],
            },
          ],
          actions: [
            "Look at the stop order on your next multi-stop run and notice the pattern.",
            "Shop the store in the same order: dry, cold, frozen.",
            "Keep the cooler where you can reach it fast, so the frozen stop is a quick handoff.",
          ],
        },
        es: {
          title: "Por qué la ruta deja lo congelado para el final",
          summary:
            "La app ordena tus paradas: primero lo no perecedero, luego lo refrigerado y al final lo congelado, para que lo más frío pase el menor tiempo posible en tu vehículo.",
          sections: [
            {
              heading: "Cómo ordena la app un recorrido",
              paragraphs: [
                "Cuando tienes varias paradas en un recorrido, la app las ordena por temperatura: primero lo no perecedero, luego lo refrigerado y al final lo congelado. Los artículos fríos pasan el menor tiempo posible en el vehículo.",
                "No siempre es el camino más corto. Es el más seguro para los alimentos.",
              ],
            },
            {
              heading: "Por qué importa",
              paragraphs: [
                "A una bolsa de arroz no le importa dar vueltas dos horas. A un helado sí. Los congelados se calientan más rápido que los refrigerados en cuanto salen del pasillo de congelados, así que recogerlos al final significa que son los primeros en salir por la puerta.",
              ],
            },
            {
              heading: "Dentro de la tienda, la misma regla",
              bullets: [
                "Compra primero los productos secos.",
                "Los refrigerados en segundo lugar.",
                "Los congelados al final, directo a la hielera.",
                "En la caja, mantén juntas las bolsas frías para que entren a la hielera de una vez.",
              ],
            },
            {
              heading: "Confía en el orden",
              paragraphs: [
                "Si sientes la tentación de reordenar las paradas para ahorrar unos minutos, piensa en lo que hay en la hielera. Una entrega echada a perder es una queja. Una ruta seguida no lo es.",
                "Si una parada no se puede completar, por ejemplo porque el cliente no está en casa, marca la excepción en la app y la app reordena lo que queda.",
              ],
            },
          ],
          actions: [
            "Observa el orden de las paradas en tu próximo recorrido con varias entregas.",
            "Recorre la tienda en el mismo orden: seco, frío, congelado.",
            "Coloca la hielera donde la alcances rápido, para que la parada de congelados sea una entrega ágil.",
          ],
        },
      },
    },
    {
      slug: "raw-meat-separation",
      related: [
        "food-safety/cold-chain-basics",
        "your-gear/coolers-and-bags",
        "reporting-a-problem/what-never-counts-against-you",
      ],
      body: {
        en: {
          title: "Raw meat separation",
          summary:
            "Raw meat, poultry, and seafood ride bagged separately and below everything else, every time.",
          sections: [
            {
              heading: "Why it matters",
              paragraphs: [
                "Juices from raw meat, poultry, and seafood can carry bacteria. If they drip onto produce, bread, or anything eaten without cooking, that is a real risk to your customer. Separation is simple, and it is not optional.",
              ],
            },
            {
              heading: "The rule",
              bullets: [
                "Bag raw meat, poultry, and seafood in their own bag at the store. One bag per type if you can.",
                "Put those bags at the bottom of the cooler, below everything else.",
                "Never stack produce, dairy, or ready-to-eat food on top of raw meat.",
                "If a package is leaking, double-bag it and wipe the cooler after the run.",
                "Wash or sanitize your hands after handling raw packages.",
              ],
            },
            {
              heading: "At the door",
              paragraphs: [
                "Hand over the raw meat bags separately and say what they are. Customers appreciate knowing where the chicken is.",
                "If a package leaked in transit and other items got wet, mark those items as an exception in the app. Do not deliver them.",
              ],
            },
            {
              heading: "Clean-up",
              paragraphs: [
                "Wipe coolers and bags with a sanitizing wipe after any run with raw meat, and let them dry open. A cooler that smells is a cooler that needs cleaning.",
              ],
            },
          ],
          actions: [
            "Put a roll of extra bags and a pack of sanitizing wipes in your vehicle.",
            "Practice the bottom-of-the-cooler habit on every run this week.",
            "Wash your reusable bags and let them dry completely.",
          ],
        },
        es: {
          title: "Separación de la carne cruda",
          summary:
            "La carne, el pollo y los mariscos crudos viajan en bolsas separadas y debajo de todo lo demás, siempre.",
          sections: [
            {
              heading: "Por qué importa",
              paragraphs: [
                "Los jugos de la carne, el pollo y los mariscos crudos pueden llevar bacterias. Si gotean sobre frutas, verduras, pan o cualquier cosa que se coma sin cocinar, es un riesgo real para tu cliente. Separarlos es sencillo, y no es opcional.",
              ],
            },
            {
              heading: "La regla",
              bullets: [
                "Embolsa la carne, el pollo y los mariscos crudos en su propia bolsa en la tienda. Una bolsa por tipo si puedes.",
                "Pon esas bolsas en el fondo de la hielera, debajo de todo lo demás.",
                "Nunca apiles frutas, verduras, lácteos o comida lista para comer encima de la carne cruda.",
                "Si un empaque gotea, ponle doble bolsa y limpia la hielera después del recorrido.",
                "Lávate o desinféctate las manos después de manejar empaques crudos.",
              ],
            },
            {
              heading: "En la puerta",
              paragraphs: [
                "Entrega las bolsas de carne cruda por separado y di qué contienen. Los clientes agradecen saber dónde va el pollo.",
                "Si un empaque goteó en el camino y otros artículos se mojaron, marca esos artículos como excepción en la app. No los entregues.",
              ],
            },
            {
              heading: "Limpieza",
              paragraphs: [
                "Limpia las hieleras y las bolsas con una toallita desinfectante después de cualquier recorrido con carne cruda, y déjalas secar abiertas. Una hielera que huele es una hielera que necesita limpieza.",
              ],
            },
          ],
          actions: [
            "Pon un rollo de bolsas extra y un paquete de toallitas desinfectantes en tu vehículo.",
            "Practica el hábito del fondo de la hielera en cada recorrido de esta semana.",
            "Lava tus bolsas reutilizables y déjalas secar por completo.",
          ],
        },
      },
    },
    {
      slug: "hot-weather-judgment",
      related: [
        "food-safety/cold-chain-basics",
        "food-safety/why-frozen-loads-last",
        "safety-on-deliveries/severe-weather",
        "your-gear/coolers-and-bags",
      ],
      body: {
        en: {
          title: "Hot-weather judgment",
          summary: "When it is hot out, the clock on cold food runs faster. Here is how to stay ahead of it.",
          sections: [
            {
              heading: "The clock runs faster",
              paragraphs: [
                "Above 90°F (32°C), perishables get about 1 hour in the danger zone, not 2. Inside a parked car it can be far hotter than outside. In summer, treat every cold item as urgent.",
              ],
            },
            {
              heading: "Before the run",
              bullets: [
                "Pre-chill your coolers. Put ice packs in the night before, or at least an hour ahead.",
                "Bring twice the ice packs you think you need.",
                "Park in shade when you can.",
                "Run the air conditioning while you drive.",
                "Fill a water bottle for yourself. Heat wears you down too.",
              ],
            },
            {
              heading: "At the door",
              paragraphs: [
                "Deliver cold items first at the door, then go back for the dry goods. If you are carrying up stairs or across a parking lot, take the cooler bag on the first trip.",
              ],
            },
            {
              heading: "Use your judgment",
              paragraphs: [
                "If the ice cream is soft, a milk jug is warm, or a package is swelling, do not deliver it. Mark it as an exception in the app. You will never be marked down for protecting a customer.",
                "Protect yourself too. Drink water, take breaks in the shade, and read the severe-weather guide before the first heat wave.",
              ],
            },
          ],
          actions: [
            "Check the forecast and add ice packs on any day above 85°F (29°C).",
            "Find the shaded parking spots at the stores you visit most.",
            "Rehearse the cold-first unload order once so it becomes automatic.",
          ],
        },
        es: {
          title: "Criterio en clima caluroso",
          summary: "Cuando hace calor, el reloj de los alimentos fríos corre más rápido. Aquí tienes cómo adelantarte.",
          sections: [
            {
              heading: "El reloj corre más rápido",
              paragraphs: [
                "Por encima de 90°F (32°C), los perecederos tienen cerca de 1 hora en la zona de peligro, no 2. Dentro de un auto estacionado puede hacer mucho más calor que afuera. En verano, trata cada artículo frío como urgente.",
              ],
            },
            {
              heading: "Antes del recorrido",
              bullets: [
                "Enfría las hieleras con anticipación. Mete los paquetes de hielo la noche anterior, o al menos una hora antes.",
                "Lleva el doble de paquetes de hielo de los que crees necesitar.",
                "Estaciona a la sombra cuando puedas.",
                "Usa el aire acondicionado mientras manejas.",
                "Llena una botella de agua para ti. El calor también te desgasta.",
              ],
            },
            {
              heading: "En la puerta",
              paragraphs: [
                "Entrega primero los artículos fríos en la puerta y luego regresa por los productos secos. Si subes escaleras o cruzas un estacionamiento, lleva la bolsa térmica en el primer viaje.",
              ],
            },
            {
              heading: "Usa tu criterio",
              paragraphs: [
                "Si el helado está blando, un envase de leche está tibio o un empaque se está inflando, no lo entregues. Márcalo como excepción en la app. Nunca te penalizarán por proteger a un cliente.",
                "Protégete tú también. Toma agua, descansa a la sombra y lee la guía de clima severo antes de la primera ola de calor.",
              ],
            },
          ],
          actions: [
            "Revisa el pronóstico y agrega paquetes de hielo cualquier día con más de 85°F (29°C).",
            "Ubica los lugares de estacionamiento con sombra en las tiendas que más visitas.",
            "Ensaya una vez el orden de descarga con lo frío primero para que se vuelva automático.",
          ],
        },
      },
    },
  ],
};
